import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { fechaLarga } from '@/lib/formato';

import { SelectorClase } from '../comun/SelectorClase';
import { SelectorEspecialidad } from '../comun/SelectorEspecialidad';
import { useResumenes, type Resumen } from './queries';

const CLAVES = ['q', 'specialtyId', 'classId', 'published', 'ultra', 'mine'] as const;

/** Alta: nombre, especialidad, clase y UltraResumen; luego abre su pantalla. */
function NuevoResumen({
  claseInicial,
  onCerrar,
}: {
  claseInicial: number | null;
  onCerrar: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: '',
    specialtyId: null as number | null,
    classId: claseInicial,
    isUltra: false,
  });
  const crear = useMutation({
    mutationFn: () =>
      unwrap<Resumen>(
        api.POST('/api/v1/admin/gpc-summaries', { body: { ...f, name: f.name.trim() } }),
      ),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ['contenido'] });
      toast.success('Resumen creado (sin publicar).');
      void navigate(`/contenido/resumenes-gpc/${d.id}`);
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <Modal abierto onCerrar={onCerrar} titulo="Nuevo resumen GPC">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          crear.mutate();
        }}
      >
        <Campo etiqueta="Nombre">
          <input
            className="input-vidrio"
            required
            minLength={2}
            maxLength={250}
            autoFocus
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Especialidad">
          <SelectorEspecialidad
            valor={f.specialtyId}
            onCambio={(specialtyId) => setF({ ...f, specialtyId })}
          />
        </Campo>
        <div>
          <span className="mb-1.5 block text-sm font-bold text-texto-2">Clase ligada</span>
          <SelectorClase valor={f.classId} onCambio={(classId) => setF({ ...f, classId })} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[var(--color-azul-600)]"
            checked={f.isUltra}
            onChange={(e) => setF({ ...f, isUltra: e.target.checked })}
          />
          UltraResumen
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button type="submit" className="btn-cs btn-primario" disabled={crear.isPending}>
            Crear y abrir
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Lista() {
  const { filtros, cambiar, query } = useFiltrosUrl(CLAVES);
  const editable = useCan('contenido', 'editar');
  const [params, setParams] = useSearchParams();
  const q = useResumenes({ ...query, page: filtros.page, pageSize: 50 });
  const nuevo = params.get('nuevo') === '1';
  const sel = (k: (typeof CLAVES)[number], opciones: [string, string][], etiqueta: string) => (
    <Campo etiqueta={etiqueta}>
      <select
        className="input-vidrio"
        value={filtros[k]}
        onChange={(e) => cambiar({ [k]: e.target.value })}
      >
        {opciones.map(([v, t]) => (
          <option key={v} value={v}>
            {t}
          </option>
        ))}
      </select>
    </Campo>
  );
  return (
    <>
      <Encabezado
        titulo="Resúmenes GPC"
        descripcion="Resúmenes de guías de práctica clínica por bloques. El alumno solo ve los disponibles y asignados a su licencia."
        acciones={
          editable && (
            <button
              type="button"
              className="btn-cs btn-primario"
              onClick={() => setParams((p) => ({ ...Object.fromEntries(p), nuevo: '1' }))}
            >
              <Plus size={16} aria-hidden /> Nuevo resumen
            </button>
          )
        }
      />
      <div className="panel-vidrio mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <Campo etiqueta="Buscar">
          <input
            className="input-vidrio"
            defaultValue={filtros.q}
            placeholder="Nombre, ID o código"
            onKeyDown={(e) => e.key === 'Enter' && cambiar({ q: e.currentTarget.value })}
            onBlur={(e) => e.target.value !== filtros.q && cambiar({ q: e.target.value })}
          />
        </Campo>
        <Campo etiqueta="Especialidad">
          <SelectorEspecialidad
            valor={filtros.specialtyId ? Number(filtros.specialtyId) : null}
            vacio="Todas"
            onCambio={(v) => cambiar({ specialtyId: v ? String(v) : '' })}
          />
        </Campo>
        <div>
          <span className="mb-1.5 block text-sm font-bold text-texto-2">Clase</span>
          <SelectorClase
            valor={filtros.classId ? Number(filtros.classId) : null}
            onCambio={(v) => cambiar({ classId: v ? String(v) : '' })}
          />
        </div>
        {sel(
          'published',
          [
            ['', 'Todos'],
            ['true', 'Disponibles'],
            ['false', 'No disponibles'],
          ],
          'Disponible',
        )}
        {sel(
          'ultra',
          [
            ['', 'Todos'],
            ['true', 'UltraResumen'],
            ['false', 'Normales'],
          ],
          'Tipo',
        )}
        {sel(
          'mine',
          [
            ['', 'De todos'],
            ['true', 'Mis resúmenes'],
          ],
          'Autor',
        )}
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.data.length === 0 && <Vacio titulo="Sin resúmenes con esos filtros" />}
      {q.data && q.data.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Especialidad</th>
                <th>Clase</th>
                <th>Bloques</th>
                <th>Estado</th>
                <th>Autor</th>
                <th>Actualizado</th>
              </tr>
            </thead>
            <tbody>
              {q.data.data.map((r) => (
                <tr key={r.id}>
                  <td className="tabular">{r.id}</td>
                  <td className="max-w-md">
                    <Link
                      to={`/contenido/resumenes-gpc/${r.id}`}
                      className="font-semibold hover:underline"
                    >
                      {r.name}
                    </Link>
                    {r.isUltra && <span className="insignia ml-2">Ultra</span>}
                  </td>
                  <td>{r.specialtyName ?? '—'}</td>
                  <td>{r.className ?? '—'}</td>
                  <td className="tabular">{r.blocksCount}</td>
                  <td>
                    {r.isPublished ? (
                      <span className="insignia insignia-activa">Disponible</span>
                    ) : (
                      <span className="insignia">Oculto</span>
                    )}
                  </td>
                  <td>{r.createdByName ?? '—'}</td>
                  <td className="whitespace-nowrap">
                    {r.updatedAt ? fechaLarga(r.updatedAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
      {nuevo && (
        <NuevoResumen
          claseInicial={params.get('classId') ? Number(params.get('classId')) : null}
          onCerrar={() =>
            setParams((p) => {
              const n = new URLSearchParams(p);
              n.delete('nuevo');
              return n;
            })
          }
        />
      )}
    </>
  );
}

export function ResumenesPage() {
  return (
    <PermissionGate seccion="contenido">
      <Lista />
    </PermissionGate>
  );
}
